import { axios } from "@/libraries/axios";
import type {
	AskHemiEnvelope,
	AskHemiShare,
	CareChat,
	CareChatMessage,
	CaseAskHemiProfile,
	GetCareChatResponse,
	WoundAnalysis,
} from "@/types/askhemi";
import type { AxiosRequestConfig } from "axios";

class AskHemiService {
	async getShare(id: number | string, config?: AxiosRequestConfig) {
		const res = await axios.get<AskHemiEnvelope<AskHemiShare>>(
			`/api/v1/askhemi_shares/${id}`,
			config,
		);
		return res.data.data;
	}

	async getCareChat(caseId: number | string, config?: AxiosRequestConfig) {
		const res = await axios.get<AskHemiEnvelope<GetCareChatResponse>>(
			`/api/v1/cases/${caseId}/care_chat`,
			config,
		);
		return res.data.data;
	}

	async startCareChat(caseId: number | string) {
		const res = await axios.post<
			AskHemiEnvelope<{ care_chat: CareChat } | CareChat>
		>(`/api/v1/cases/${caseId}/care_chat`);
		const data = res.data.data;
		return "care_chat" in data ? data.care_chat : data;
	}

	async closeCareChat(caseId: number | string) {
		const res = await axios.post<
			AskHemiEnvelope<{ care_chat: CareChat } | CareChat>
		>(`/api/v1/cases/${caseId}/care_chat/close`);
		const data = res.data.data;
		return "care_chat" in data ? data.care_chat : data;
	}

	async getCareChatMessages(
		caseId: number | string,
		afterId?: number,
		config?: AxiosRequestConfig,
	) {
		const res = await axios.get<
			AskHemiEnvelope<CareChatMessage[] | { messages: CareChatMessage[] }>
		>(`/api/v1/cases/${caseId}/care_chat/messages`, {
			...config,
			params: afterId ? { after_id: afterId } : undefined,
		});
		const data = res.data.data;
		return Array.isArray(data) ? data : (data?.messages ?? []);
	}

	async sendCareChatMessage(caseId: number | string, text: string) {
		const res = await axios.post<
			AskHemiEnvelope<CareChatMessage | { message: CareChatMessage }>
		>(`/api/v1/cases/${caseId}/care_chat/messages`, { text });
		const data = res.data.data;
		return "message" in data ? data.message : data;
	}

	async createWoundAnalysis(
		caseId: number | string,
		askhemiShareId: number | string,
	) {
		const res = await axios.post<AskHemiEnvelope<WoundAnalysis>>(
			`/api/v1/cases/${caseId}/wound_analyses`,
			{ askhemi_share_id: askhemiShareId },
		);
		return res.data.data;
	}

	async getWoundAnalysis(id: number | string, config?: AxiosRequestConfig) {
		const res = await axios.get<AskHemiEnvelope<WoundAnalysis>>(
			`/api/v1/wound_analyses/${id}`,
			config,
		);
		return res.data.data;
	}

	async listWoundAnalyses(
		caseId: number | string,
		config?: AxiosRequestConfig,
	) {
		const res = await axios.get<AskHemiEnvelope<WoundAnalysis[]>>(
			`/api/v1/cases/${caseId}/wound_analyses`,
			config,
		);
		return res.data.data;
	}

	async getCaseAskHemiProfile(
		caseId: number | string,
		config?: AxiosRequestConfig,
	) {
		const res = await axios.get<AskHemiEnvelope<CaseAskHemiProfile>>(
			`/api/v1/cases/${caseId}/askhemi_profile`,
			config,
		);
		return res.data.data;
	}
}

export const askHemiService = new AskHemiService();
